import {
	AsyncSelect,
	Box,
	Button,
	CheckboxInput,
	Dropdown,
	Icons,
	Inline,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import {
	CursorListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import {
	ISimpleListResponse,
	ICursorListResponse,
	useXHR,
	XHRInstance,
	IListResponse,
} from "@sembark-travel/xhr"
import React, {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { Omit } from "utility-types"
import { TTripDestination } from "../TripDestinations"
import { IRoomType } from "./store"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { Link, useLocationQuery } from "@sembark-travel/ui-router"
import { generatePath } from "../router-utils"
import {
	Form,
	isTruthy,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { checkFilterAppliedFn } from "../utils"
import * as validator from "yup"
import useSWR from "swr"

function XHR(xhr: XHRInstance) {
	return {
		async getRoomTypes(
			params?: unknown
		): Promise<ISimpleListResponse<IRoomType>> {
			return xhr.get("/room-types", { params }).then((resp) => resp.data)
		},

		async getRoomTypesWithTotalRooms(
			params?: Record<string, unknown>
		): Promise<IListResponse<IRoomType>> {
			return xhr
				.get("/room-types", {
					params: {
						pagination: "paginate",
						...params,
					},
				})
				.then((resp) => resp.data)
		},

		async destroy(roomTypeId: number | string) {
			return xhr
				.delete<{ message: string }>(`/room-types/${roomTypeId}`)
				.then((resp) => resp.data)
		},
		async getRoomTypesWithTotal(
			params?: Record<string, unknown>
		): Promise<ICursorListResponse<IRoomType>> {
			return xhr
				.get("/room-types", { params: { ...params, pagination: "cursor" } })
				.then((resp) => resp.data)
		},
	}
}

type TFilters = TSearchParams & {
	unused?: 1 | 0
}

type TFiltersInLocationQuery = TSearchParams & {
	unused?: 1 | 0
}

function filtersToQuery(filters: TFilters): TFiltersInLocationQuery {
	const { unused, q, page } = filters
	const query: TFiltersInLocationQuery = {}
	if (q) {
		query.q = q
	}

	if (page) {
		query.page = page
	}
	if (unused) {
		query.unused = 1
	}
	return query
}

function queryToFilters(query: TFiltersInLocationQuery): TFilters {
	const { unused, q, page } = query

	const filters: TFilters = {}

	if (q) {
		filters.q = q
	}
	if (page) {
		filters.page = page
	}
	if (unused) {
		filters.unused = 1
	}
	return filters
}

function filtersToAPIParams(params: TFilters) {
	return {
		...params,
		unused: isTruthy(params.unused) ? 1 : 0,
	}
}

function Filters() {
	return <SwitchInputField name="unused" label="Unused Rooms Only" />
}

export function List() {
	const [query, setQuery] = useLocationQuery<TFilters, TFiltersInLocationQuery>(
		{
			toQuery: filtersToQuery,
			fromQuery: queryToFilters,
		}
	)
	const [params, setParams] = useSearch<TFilters>(query)
	const [componentKey, setComponentKey] = useState(0)

	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Fragment>
			<Search
				initialParams={params}
				onSearch={(params) => {
					setParams({ ...params, cursor: null })
				}}
				title="Room Types"
				actions={({ changeAdvancedFiltersVisibility }) => (
					<Inline gap="4">
						<Dropdown alignRight>
							<Dropdown.ToggleButton level="tertiary">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								<BulkDeleteRoomTypes
									onSuccess={() => {
										setComponentKey((key) => ++key)
									}}
									params={params}
									key={componentKey}
									FilterOpen={() => {
										changeAdvancedFiltersVisibility(true)
									}}
								>
									{({ onDelete }) => (
										<Dropdown.MenuItem onClick={onDelete}>
											<Icons.Trash color="danger" /> Bulk Delete Unused Rooms
										</Dropdown.MenuItem>
									)}
								</BulkDeleteRoomTypes>
							</Dropdown.Menu>
						</Dropdown>
					</Inline>
				)}
				Filters={Filters}
				areAdvancedFiltersApplied={(params) => {
					const { unused } = params
					return isTruthy(unused)
				}}
			>
				<CursorListView<IRoomType>
					pageKey="room-types"
					fetch={(xhr, params) =>
						XHR(xhr).getRoomTypesWithTotal({
							...filtersToAPIParams(params),
							include: "hotels_count",
						})
					}
					onCursorChange={(cursor) => setParams({ ...params, cursor })}
					params={params}
					key={componentKey}
				>
					{({ items: roomTypes, refresh, xhr }) => (
						<Table
							striped
							responsive
							bordered
							headers={["Name", "Description", "Hotels", ""]}
							alignCols={{ 2: "center", 3: "right" }}
							rows={roomTypes.map((roomType) => [
								<Stack>
									<Text>{roomType.display_name}</Text>
									{roomType.short_name ? (
										<Text color="muted" fontSize="sm" title="Short Name">
											{roomType.short_name}
										</Text>
									) : null}
								</Stack>,
								roomType.description,
								roomType.hotels_count ? (
									<Link
										to={generatePath("/org/hotels")}
										query={{
											room_types: [roomType.id + "_" + roomType.name],
										}}
									>
										{roomType.hotels_count}
									</Link>
								) : (
									0
								),
								<Button
									status="warning"
									size="sm"
									level="tertiary"
									onClick={() => {
										if (
											window.confirm(
												"Are you sure you want to delete this room type?\n\nThis will be deleted and removed from all hotels. Existing quotation and bookings will not be affected.\n\nThis action CAN NOT be reverted."
											)
										) {
											XHR(xhr)
												.destroy(roomType.id)
												.then((resp) => {
													showSnackbar(
														resp.message || "Room Type deleted successfully."
													)
													refresh()
												})
												.catch((e) => {
													const error: Error = e
													window.alert(error.message || "Something went wrong")
												})
										}
									}}
								>
									<Icons.Trash />
								</Button>,
							])}
						/>
					)}
				</CursorListView>
			</Search>
		</Fragment>
	)
}

export function SelectRoomTypes({
	tripDestinations,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	tripDestinations?: Array<TTripDestination>
}) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			multiple
			cacheKey={`room-types-${tripDestinations?.map((t) => t.id).join("-")}`}
			{...props}
			optionRenderer={({ option, created }) => {
				if (created) return <Text>Add "{option.name}"</Text>
				const roomType: IRoomType = option
				return (
					<Stack>
						<Text>{roomType.display_name || roomType.name}</Text>
						{roomType.short_name ? (
							<Text fontSize="sm" color="muted">
								{roomType.short_name}
							</Text>
						) : null}
					</Stack>
				)
			}}
			fetch={(q) =>
				XHR(xhr)
					.getRoomTypes({
						q,
						trip_destinations: tripDestinations?.length
							? tripDestinations.map((t) => t.id)
							: null,
					})

					.then((resp) => resp.data)
			}
		/>
	)
}

interface RefHandle {
	getUnSelectedIds: () => number[]
	isItemZero: boolean
}

function BulkDeleteRoomTypes({
	children,
	params,
	onSuccess,
	FilterOpen,
}: {
	children: (props: { onDelete: () => void }) => React.ReactNode
	params: TFilters
	onSuccess: () => void
	FilterOpen: () => void
}) {
	const xhr = useXHR()
	const [isOpen, openDialog, closeDialog] = useDialog()
	const inProgressRef = useRef(false)
	const [inProgressState, setInProgressState] = useState(false)
	const [isSelectAll, setIsSelectAll] = useState(false)
	const childRef = useRef<RefHandle>(null)
	const areFiltersApplied = useMemo(
		() => checkFilterAppliedFn(params, "cursor", "page"),
		[params]
	)
	const [count, setCount] = useState({
		count: 0,
		totalCount: 0,
	})

	useEffect(() => {
		if (!inProgressRef.current && inProgressState) {
			showSnackbar("Deleting...")
		}
		if (inProgressRef.current && !inProgressState) {
			showSnackbar("Deleted Successfully...")
			closeDialog()
		}
		inProgressRef.current = inProgressState
	}, [inProgressState, closeDialog])

	useEffect(() => {
		async function getCount() {
			try {
				const item = await XHR(xhr).getRoomTypesWithTotalRooms({
					...filtersToAPIParams(params),
					limit: 1,
					page: 1,
				})
				setCount({
					count: 0,
					totalCount: item.meta.total || 0,
				})
			} catch (e) {
				const error = e as Error
				window.alert(error.message)
			}
		}

		if (isOpen && areFiltersApplied) {
			getCount()
		}

		if (!isOpen) {
			setCount({
				count: 0,
				totalCount: 0,
			})
			setIsSelectAll(false)
		}
	}, [isOpen, params, areFiltersApplied, xhr])

	const decreaseIncrease = (type: "decrease" | "increase") => {
		setCount((val) => ({
			...val,
			count: type === "decrease" ? --val.count : ++val.count,
		}))
	}

	const openFilter = () => {
		closeDialog()
		FilterOpen()
	}
	return (
		<Batch onFinish={() => onSuccess()}>
			{({ init, xhr, inProgress }) => {
				if (inProgress !== inProgressState) {
					setInProgressState(inProgress)
				}
				return (
					<>
						{children({ onDelete: () => openDialog() })}
						<Dialog
							title="Bulk Delete Unused Rooms"
							open={isOpen}
							onClose={closeDialog}
							lg
						>
							<Form<{ delete_rooms_count: number }>
								validate={validateFormValues(
									validator.object().shape({
										delete_rooms_count: validator
											.number()
											.equals(
												[count.count],
												`Please type ${count.count} to confirm the selection`
											)
											.not([0], "Please type a valid number")
											.required(
												`Please type ${count.count} to confirm the selection`
											),
									})
								)}
								onSubmit={withServerErrors(async () => {
									const resp = await xhr.delete("/room-types-bulk-disable", {
										data: {
											...filtersToAPIParams(params),
											...(isSelectAll
												? {
														excluded_ids: childRef.current
															?.getUnSelectedIds()
															.map((id) => id),
													}
												: {
														included_ids: childRef.current
															?.getUnSelectedIds()
															.map((id) => id),
													}),
										},
									})
									init(resp.data.data)
								})}
								subscription={{ submitting: true }}
							>
								{({ handleSubmit, submitting }) => (
									<form onSubmit={handleSubmit}>
										<Dialog.Body>
											<Inline justifyContent="between">
												<Text fontSize="lg" fontWeight="semibold">
													Please review details
												</Text>
												{!isSelectAll &&
												areFiltersApplied &&
												!childRef.current?.isItemZero ? (
													<Inline alignItems="center">
														<CheckboxInput
															checked={isSelectAll}
															name="selected-all"
															id="selected_all"
															onChange={(e) => {
																setIsSelectAll(true)
																setCount((val) => ({
																	...val,
																	count: val.totalCount,
																}))
															}}
														/>

														<Box as="label" htmlFor="selected_all">
															Select All
														</Box>
													</Inline>
												) : null}
											</Inline>
											<InfiniteScroll
												params={params}
												openFilter={openFilter}
												isSelectAll={isSelectAll}
												ref={childRef}
												decreaseIncrease={decreaseIncrease}
											/>
											{areFiltersApplied && !childRef.current?.isItemZero ? (
												<Stack gap="4">
													<Text color="danger">
														<Icons.Info /> Deleting these room types are
														permanent. You can't restore them later.
													</Text>
													{count.count ? (
														<TextInputField
															name="delete_rooms_count"
															label={`Please Type ${count.count} to confirm`}
															type="number"
															required
														/>
													) : null}
												</Stack>
											) : null}
										</Dialog.Body>
										{areFiltersApplied && !childRef.current?.isItemZero ? (
											<Dialog.Footer>
												<Stack gap={"4"}>
													<SubmissionError />
													<Inline gap={"2"}>
														<Button
															type="submit"
															disabled={submitting || !count.count}
															status="danger"
														>
															<Icons.Ok /> I understand, delete these Rooms
														</Button>
														<Button
															level="tertiary"
															onClick={() => closeDialog()}
														>
															Cancel
														</Button>
													</Inline>
												</Stack>
											</Dialog.Footer>
										) : null}
									</form>
								)}
							</Form>
						</Dialog>
					</>
				)
			}}
		</Batch>
	)
}

type TInfiniteScrollBase = {
	params: TFilters
	openFilter: () => void
	isSelectAll: boolean
	decreaseIncrease: (type: "decrease" | "increase") => void
}

type CursorMeta = ICursorListResponse<unknown>["meta"]

const InfiniteScroll = React.forwardRef<RefHandle, TInfiniteScrollBase>(
	InfiniteScrollBase
)

function InfiniteScrollBase(
	{ params, decreaseIncrease, openFilter, isSelectAll }: TInfiniteScrollBase,
	ref: React.ForwardedRef<RefHandle>
) {
	const groupRef = useRef<HTMLDivElement>(null)
	const pageInfo = useRef<CursorMeta>()
	const isFetchingRef = useRef(false)
	const SCROLL_THRESHOLD = 100
	const [items, setItems] = useState<IRoomType[]>([])
	const [selectedItems, setSelectedItmes] = useState<number[]>([])

	type Status = {
		isLoading: boolean
		error: string
	}

	const [status, setStatus] = useState<Status>({
		isLoading: false,
		error: "",
	})
	const xhr = useXHR()
	const getUnusedRoomTypes = useCallback(async () => {
		if (isFetchingRef.current) return
		setStatus((val) => ({
			...val,
			isLoading: true,
		}))
		isFetchingRef.current = true
		try {
			const item = await XHR(xhr).getRoomTypesWithTotal({
				...params,
				limit: 10,
				cursor: pageInfo.current?.next_cursor,
			})
			pageInfo.current = item.meta

			setItems((prev) =>
				pageInfo.current?.prev_cursor ? [...prev, ...item.data] : [...item.data]
			)
			setStatus((val) => ({ error: "", isLoading: false }))
		} catch (e) {
			const error = e as Error
			setStatus((val) => ({
				...val,
				error: error.message || "Something went Wrong",
			}))
		} finally {
			isFetchingRef.current = false
		}
	}, [xhr, params])

	useEffect(() => {
		if (checkFilterAppliedFn(params, "cursor", "page")) {
			getUnusedRoomTypes()
		}
		let throttleTimeout: ReturnType<typeof setTimeout> | null = null

		const throttledHandleScroll = () => {
			if (throttleTimeout) return
			throttleTimeout = setTimeout(() => {
				const container = groupRef.current?.querySelector("ol")
				if (!container) return

				const isBottom =
					container.scrollHeight - container.scrollTop <=
					container.clientHeight + SCROLL_THRESHOLD

				if (
					isBottom &&
					!isFetchingRef.current &&
					pageInfo.current?.next_cursor
				) {
					getUnusedRoomTypes()
				}
				throttleTimeout = null
			}, 300)
		}

		const container = groupRef.current?.querySelector("ol")
		if (container) {
			container.addEventListener("scroll", throttledHandleScroll)
			return () => {
				container.removeEventListener("scroll", throttledHandleScroll)
				if (throttleTimeout) {
					clearTimeout(throttleTimeout)
				}
			}
		}
	}, [getUnusedRoomTypes, params])

	React.useImperativeHandle(ref, () => ({
		getUnSelectedIds: function () {
			return selectedItems
		},
		isItemZero: !items.length && !status.isLoading,
	}))

	useEffect(() => {
		setSelectedItmes([])
	}, [isSelectAll])

	return (
		<Stack
			padding="4"
			borderWidth="1"
			borderColor="warning"
			rounded="lg"
			marginBottom="4"
			ref={groupRef}
		>
			{status.error ? (
				<Box
					display="flex"
					alignItems="center"
					gap={"3"}
					padding={"3"}
					borderColor={"danger"}
					borderRadius="md"
				>
					<Box as="span" flex="1" fontWeight="semibold">
						{status.error}
					</Box>
					<Box
						as="button"
						paddingX={"3"}
						paddingY={"1"}
						color="white"
						borderRadius="md"
						fontWeight="semibold"
						onClick={getUnusedRoomTypes}
					>
						Refresh
					</Box>
				</Box>
			) : null}
			<Box
				style={{
					height: "400px",
				}}
				as="ol"
				overflowY="auto"
				display={!items.length && !status.isLoading ? "flex" : "block"}
				flexDirection={"col"}
				justifyContent={"center"}
				alignItems={"center"}
			>
				{!items.length && !status.isLoading ? (
					<>
						<Box as="p">
							Please choose right filter or search option to delete any room
						</Box>
						<Button type="button" onClick={openFilter}>
							Choose Right Filter
						</Button>
					</>
				) : (
					<Table
						bordered
						responsive
						hover
						headers={["", "Name", "Description"]}
						rows={items.map((room) => {
							const { name, description, id } = room
							return [
								<CheckboxInput
									id={name + id}
									checked={
										isSelectAll
											? !selectedItems.includes(id)
											: selectedItems.includes(id)
									}
									onChange={(e) => {
										const checked =
											(e.currentTarget.checked && isSelectAll) ||
											(!e.currentTarget.checked && !isSelectAll)

										if (checked) {
											setSelectedItmes((ids) =>
												ids.filter((singleId) => singleId !== id)
											)
											decreaseIncrease(isSelectAll ? "increase" : "decrease")
										} else {
											setSelectedItmes((ids) => [...ids, id])
											decreaseIncrease(!isSelectAll ? "increase" : "decrease")
										}
									}}
								/>,
								<Text>{name}</Text>,
								<Text as="p">{description}</Text>,
							]
						})}
					/>
				)}

				{status.isLoading ? (
					<Inline justifyContent="center" gap="2" alignItems="center">
						Loading more rooms...
					</Inline>
				) : null}
			</Box>
		</Stack>
	)
}

export type TBatch = {
	id: string
	name: string
	totalJobs: number
	pendingJobs: number
	processedJobs: number
	progress: number
	failedJobs: number
	createdAt: string
	cancelledAt: string | null
	finishedAt: string | null
}

export function Batch({
	children,
	onFinish,
}: {
	children: (props: {
		init: (batch: TBatch) => void
		xhr: XHRInstance
		inProgress: boolean
	}) => React.ReactNode
	onFinish: () => void
}) {
	const xhr = useXHR()
	const [batch, setBatch] = useState<TBatch | null>(null)
	const { data: currentBatch } = useSWR(
		batch ? `/batch/${batch.id}` : null,
		() =>
			xhr
				.get<{ data: TBatch }>(`batches/${batch?.id}`)
				.then((resp) => resp.data.data)
				.then((batch) => {
					if (batch.finishedAt) {
						onFinish()
					}
					return batch
				}),
		{
			refreshInterval: 1000,
		}
	)
	return (
		<>
			{children({
				xhr,
				init: (batch) => setBatch(batch),
				inProgress: Boolean(currentBatch && !currentBatch.finishedAt),
			})}
		</>
	)
}
